import styled from '@emotion/styled';
import { palette, shadow } from '../../style-utils/theme';

/**
 * Lobbero brand mark — a stylized door / archway, matching the lobbero_web
 * `.logo-mark` exactly: the same rounded-top tile (border-radius 9/9/3/3 on a
 * 32px grid, expressed here as ratios so it holds at any size), in the same
 * Lagoon Teal, with the doorway cut into its base. Always pair it to the LEFT
 * of the full "Lobbero" wordmark (never split the name).
 *
 *   <LogoMark /> <span>Lobbero</span>
 *
 * public/assets/ holds the rendered icon set for this same geometry — the
 * SVG favicon, the .ico, the apple-touch icon and the maskable PWA icons —
 * and those files are byte-identical to lobbero_web/assets/. The mark is one
 * object everywhere it appears: this component, the browser tab, the home
 * screen and the marketing site.
 *
 * Props:
 *   size — px (default 32); the doorway scales proportionally.
 *   tone — "light" (default): teal tile + mint doorway, for light surfaces
 *          (dashboard header, sidebars).
 *          "onBrand": white tile + teal doorway, for the dark brand gradient
 *          panel (login hero).
 */
export const LogoMark = styled.span<{
  size?: number;
  tone?: 'light' | 'onBrand';
}>`
  position: relative;
  flex: 0 0 auto;
  display: inline-block;
  width: ${({ size = 32 }) => size}px;
  height: ${({ size = 32 }) => size}px;
  border-radius: ${({ size = 32 }) =>
    `${size * 0.28}px ${size * 0.28}px ${size * 0.09}px ${size * 0.09}px`};
  background: ${({ tone }) =>
    tone === 'onBrand'
      ? palette.white
      : `linear-gradient(155deg, ${palette.irisGlow} 0%, ${palette.iris} 50%, ${palette.irisDark} 100%)`};
  /* Tokens, and ink rather than the old rgba(18, 16, 34, .24) — that value is
     #121022, the Deep Iris purple ink the product retired. It was the last
     purple left in the UI. */
  box-shadow: ${({ tone }) =>
    tone === 'onBrand'
      ? `${shadow.md}, ${shadow.sheenStrong}`
      : `${shadow.brand}, ${shadow.sheen}`};

  /* The doorway / archway opening. */
  &::after {
    content: '';
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: ${({ size = 32 }) => size * 0.375}px;
    height: ${({ size = 32 }) => size * 0.5}px;
    border-radius: ${({ size = 32 }) =>
      `${size * 0.19}px ${size * 0.19}px 0 0`};
    background: ${({ tone }) =>
      tone === 'onBrand' ? palette.iris : palette.porcelain};
  }
`;
